/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { Heading, HelpTip, NumberField } from 'sarsaparilla';

import { GROUP_SIZE_PROP_PATH } from 'ui-permit/shared-components/constants/propPaths';

const defaultDataBindingMap = {
    [GROUP_SIZE_PROP_PATH]: 'extra_fields.group_size',
};

export default function GroupSizeHOC(_, dataBindingsMap = defaultDataBindingMap) {
    const dm = { ...defaultDataBindingMap, ...dataBindingsMap };

    const groupSizePropPath = () => `${dm[GROUP_SIZE_PROP_PATH]}`;

    const defaultInstructionText = (
        <span>
            Please indicate the total number of people in your group.
            <HelpTip
                tipForDescriptor="basic usage example"
                maxWidth={240}
                content="To ensure you're including all members of the group, please check this location’s detailed content about group size. If you're using a guide service, some locations require you to include the guide in your total group size."
            />
        </span>
    );

    function GroupSize({
        instructionText = defaultInstructionText,
        isReadOnly,
        minSize = 1,
        maxSize = 99,
        reservation,
        onGroupSizeChange,
    }) {
        const getGroupSize = () => get(reservation, groupSizePropPath(), 1);

        const groupMemberSize = reservation?.group_members?.length || 0;
        useEffect(() => {
            if (groupMemberSize === 0) {
                onGroupSizeChange(getGroupSize());
            }
        }, [groupMemberSize]);

        useEffect(() => {
            if (minSize > getGroupSize()) onGroupSizeChange(minSize);
        }, [reservation, minSize]);

        return (
            <div className="per-group-size-form per-section-group">
                <Heading headingLevel={2} appearance="h5" className="mb-2">
                    Group Members
                </Heading>
                <p>{instructionText}</p>
                <NumberField
                    id="group-size-number-field"
                    isDisabled={isReadOnly}
                    min={minSize}
                    max={maxSize}
                    onChange={onGroupSizeChange}
                    value={getGroupSize()}
                    isRequired
                    visualLabel="Total Group Members"
                    visualLabelPosition="top"
                    units="members"
                />
            </div>
        );
    }

    GroupSize.propTypes = {
        instructionText: PropTypes.node,
        isQuotaModifyAllowed: PropTypes.bool,
        isReadOnly: PropTypes.bool,
        minSize: PropTypes.number,
        maxSize: PropTypes.number,
        reservation: PropTypes.object,
        onGroupSizeChange: PropTypes.func,
        onQuotaModify: PropTypes.func,
        buttonText: PropTypes.string,
    };

    // this is required for the `.name` attribute
    return GroupSize;
}
